/**
 * `/settings` — TASK 7.4 Step 4.
 *
 * Extracted from the mock's `rSettings` block (L629-646) + `_settings()`
 * (L1118-1121). The identity panel is verbatim:
 *
 *   section[data-screen-label="Settings"]
 *     h1  .sm-page-title --shadow-purple [data-h1]        "YOU"           L631
 *     div .sm-panel .sm-panel--profile                                     L632
 *       span .sm-avatar .sm-avatar--lg                    initial          L633
 *       span .sm-flex-fill                                                 L634
 *         span .sm-identity__name                         display name
 *         span .sm-identity__email                        email
 *         span .sm-pill .sm-pill--role                    ADMIN | MEMBER
 *       button .sm-btn .sm-btn--signout                   "Sign out"       L635
 *
 * ── SIGN-OUT ORDERING (AC 1.5) — the part that matters ────────────────────
 * `POST /api/auth/signout` clears the server session cookie and returns the
 * purge directive. The local erase runs in a `finally`, so it happens **even
 * when the API call fails**. Anything else is a data-leak bug: a user signing
 * out on a flaky connection (or in a tunnel, or with the API down) would keep
 * their downloaded audio and cached personal records on a device they believe
 * they just signed out of. `apps/web/test/sign-out-purge.test.ts` pins both the
 * happy path and the network-failure path, and asserts the failure is LOGGED
 * rather than swallowed.
 *
 * The erase itself is `lib/local-purge.ts#purgeLocalState` — literally the same
 * function object the api-client's 403 revocation handler calls (AC 2.5), so
 * the two erase paths cannot drift.
 *
 * ── DEV-ONLY TOGGLES (mock DOM deltas #4 and #5) ──────────────────────────
 * The mock's "DEMO SWITCHES" section (h2 + caption + three toggle rows, L637-643)
 * and its footer (L644, "…auth is simulated, no real OAuth anywhere") drove the
 * deleted simulation layer and make a claim that is now FALSE. Both live in
 * `src/dev/settings-dev-toggles.js`, imported DYNAMICALLY behind
 * `import.meta.env.DEV`, so Rollup drops the module, its chunk, and the
 * `__steamyDevFlags` / `?fail=` identifiers from every production bundle
 * (TASK 1.6's prod-bundle grep, Security N2). In DEV the section renders in
 * full, keeping the fidelity crawl's /settings testids intact.
 *
 * ── HONEST GAP: `GET /api/me` returns no email or display name ────────────
 * `meResponseSchema` is `{ userId, role, csrfToken }` — there is no email or
 * name to render. So the name falls back to the mock's OWN derivation
 * (`admin ? 'You (Owner)' : 'You'`) and the email line renders EMPTY rather
 * than hardcoding the mock's `you@steamymusic.app`. Shipping a fake address
 * would be worse than an empty line. Server follow-up noted in the task report.
 *
 * @module
 */

import { TESTIDS } from '@streamymusic/shared';

import { append, el } from '../../components/primitives/dom.js';
import { Button } from '../../components/primitives/Button.js';
import { Pill } from '../../components/primitives/Pill.js';
import { avatarInitial } from '../../app/Shell.js';
import { PageHeading, navigate, routeById } from '../../app/router.js';
import { apiClient } from '../../../api/api-client.js';
import { APP_BUILD_ID, appVersionLabel } from '../../../lib/app-version.js';
import { purgeLocalState } from '../../../lib/local-purge.js';

/**
 * Runs the full sign-out: server first, local erase ALWAYS, then `/signin`.
 *
 * @param {{
 *   api?: { signOut: () => Promise<void> },
 *   purge?: () => Promise<unknown>,
 *   go?: (hash: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 * }} [deps]
 * @returns {Promise<{ apiFailed: boolean }>}
 */
export async function signOut(deps = {}) {
  const {
    api = apiClient,
    purge = purgeLocalState,
    go = navigate,
    warn = (message, detail) => {
      console.warn(message, detail);
    },
  } = deps;

  let apiFailed = false;
  try {
    await api.signOut();
  } catch (error) {
    apiFailed = true;
    // Never swallowed: a sign-out that did not reach the server is an event an
    // operator needs to see. Error TYPE only — no tokens, no cookies, no PII.
    warn('sign-out: server call failed; erasing local state anyway', {
      reason: error instanceof Error ? error.name : 'unknown',
    });
  } finally {
    // AC 1.5 — the erase is unconditional.
    await purge();
    go('#/signin');
  }
  return { apiFailed };
}

/**
 * "App version" — which build this device actually loaded.
 *
 * Behind a service worker the running code is not necessarily the deployed code,
 * so without this there is no way to tell "the fix doesn't work" from "the fix
 * isn't loaded" (see `lib/app-version.ts`). The commit is the same SHA CI tags
 * the Cloud Run image with, so it can be compared straight against a deploy.
 *
 * @returns {HTMLElement}
 */
export function AppVersionRow() {
  const row = el('div', { className: 'sm-panel', testId: 'app-version' });
  return append(
    row,
    append(
      el('span', { className: 'sm-flex-fill' }),
      el('span', { className: 'sm-identity__name', text: 'App version' }),
      el('span', { className: 'sm-identity__email', text: `build ${APP_BUILD_ID}` }),
    ),
    // The comparable bit gets the emphasis; the cache-rotation id stays as the
    // quieter second line above, where it is useful only when debugging caches.
    el('span', { className: 'sm-identity__name', text: appVersionLabel() }),
  );
}

/** The four states {@link WakeLockRow} renders, as user-facing copy. */
export const WAKE_LOCK_COPY = {
  held: {
    pill: 'ON',
    detail: 'The screen won’t sleep while you’re in the app.',
    color: 'var(--sm-color-teal)',
  },
  waiting: {
    pill: 'WAITING',
    detail: 'Tap anywhere to turn it on — iPhone only allows this after you touch the screen.',
    color: 'var(--sm-color-yellow)',
  },
  off: { pill: 'OFF', detail: 'Not currently requested.', color: 'var(--sm-color-purple)' },
  unsupported: {
    pill: 'N/A',
    detail: 'This device or browser can’t keep the screen awake (needs iOS 18.4+ when installed).',
    color: 'var(--sm-color-pink)',
  },
};

/**
 * "Screen stays awake" — a readout, not a control.
 *
 * WHY IT EXISTS. Keeping the screen awake is invisible when it works and
 * invisible when it fails, so a report of "it doesn't seem to work" could not be
 * confirmed or denied from the outside: every refusal was swallowed into a
 * console nobody reads on a phone. This row states what is actually true, and
 * distinguishes the two failures that need completely different responses —
 * "the platform will never allow this" from "the platform hasn't allowed it
 * YET, tap the screen" (`lib/wake-lock.ts`, Safari's user-activation rule).
 *
 * The refresh is a ONE-SHOT `pointerdown` listener rather than a timer: the
 * `waiting -> held` transition is caused by exactly that gesture, `{once: true}`
 * removes itself so nothing outlives the screen (this view has no dispose hook),
 * and reaching /settings normally requires taps — so the row is usually already
 * reading `held` by the time it is read.
 *
 * @param {() => import('../../../lib/wake-lock.js').WakeLockStatus} readStatus
 * @param {Document} doc
 * @returns {HTMLElement}
 */
export function WakeLockRow(readStatus, doc) {
  const detail = el('span', { className: 'sm-identity__email' });
  const pillHost = el('span');
  const label = el('span', { className: 'sm-identity__name', text: 'Screen stays awake' });

  function paint() {
    const status = readStatus();
    const copy = WAKE_LOCK_COPY[status.state] ?? WAKE_LOCK_COPY.off;
    // The rejection name is appended only when it adds information a user (or a
    // bug report) can act on — an expected pre-gesture refusal is not news.
    const suffix =
      status.lastError !== null && status.state !== 'held' && status.state !== 'waiting'
        ? ` (${status.lastError})`
        : '';
    detail.textContent = `${copy.detail}${suffix}`;
    pillHost.replaceChildren(
      Pill({ label: copy.pill, tone: 'role', background: copy.color }),
    );
  }

  paint();
  doc.addEventListener('pointerdown', () => paint(), { once: true, passive: true });

  const row = el('div', {
    className: 'sm-panel',
    testId: 'wake-lock-status',
  });
  return append(row, append(el('span', { className: 'sm-flex-fill' }), label, detail), pillHost);
}

/**
 * Builds the `/settings` screen body (the shell provides header/nav).
 *
 * @param {{
 *   session: { role: 'admin'|'member'|null, user: { email?: string, name?: string }|null },
 *   onSignOut?: () => void,
 *   dev?: boolean,
 *   wakeLockStatus?: () => import('../../../lib/wake-lock.js').WakeLockStatus,
 *   doc?: Document,
 * }} options
 *   `wakeLockStatus` makes the keep-the-screen-awake feature OBSERVABLE — see
 *   {@link WakeLockRow}. Omitted, the row is simply not rendered.
 * @returns {HTMLElement}
 */
export function Settings(options) {
  const {
    session,
    onSignOut,
    dev = Boolean(import.meta.env.DEV),
    wakeLockStatus,
    doc = document,
  } = options;
  const isAdmin = session.role === 'admin';

  const section = el('section', { attrs: { 'data-screen-label': 'Settings' } });
  const route = routeById('settings');
  if (route === undefined) throw new Error('Settings: the /settings route is missing');
  append(section, PageHeading(route)); // L631 — "YOU"

  const identity = el('span', { className: 'sm-flex-fill' }); // L634
  append(
    identity,
    el('span', {
      className: 'sm-identity__name',
      // mock `_settings()` L1118: admin ? 'You (Owner)' : 'You'
      text: session.user?.name ?? (isAdmin ? 'You (Owner)' : 'You'),
    }),
    el('span', { className: 'sm-identity__email', text: session.user?.email ?? '' }),
    Pill({
      label: isAdmin ? 'ADMIN' : 'MEMBER',
      tone: 'role',
      background: isAdmin ? 'var(--sm-color-pink)' : 'var(--sm-color-teal)',
    }),
  );

  const panel = el('div', { className: 'sm-panel sm-panel--profile' }); // L632
  append(
    panel,
    el('span', { className: 'sm-avatar sm-avatar--lg', text: avatarInitial(session.user) }), // L633
    identity,
    Button({
      label: 'Sign out',
      variant: 'primary',
      className: 'sm-btn--signout',
      testId: TESTIDS.SIGN_OUT_BUTTON,
      onClick: () => {
        if (onSignOut !== undefined) onSignOut();
        else void signOut();
      },
    }), // L635
  );
  append(section, panel);

  // The keep-the-screen-awake readout (see WakeLockRow for why it is a readout).
  if (wakeLockStatus !== undefined) append(section, WakeLockRow(wakeLockStatus, doc));

  // Which build is loaded — unconditional: it is the first thing any on-device
  // report needs, and it depends on nothing being wired in.
  append(section, AppVersionRow());

  // `import.meta.env.DEV &&` FIRST and literally: Vite substitutes `false`
  // there in a production build, so Rollup const-folds the whole condition and
  // drops the branch, the dynamic import and its chunk. Writing `if (dev)`
  // alone would NOT be statically false (the parameter is caller-supplied) and
  // the toggles would ship as a lazy chunk — which the TASK 1.6 prod-bundle
  // grep for `__steamyDevFlags` / `?fail=` would then find.
  if (import.meta.env.DEV && dev) {
    // Async, so the identity panel above paints first.
    void import('../../../dev/settings-dev-toggles.js')
      .then((module) => {
        append(section, ...module.DevToggles());
      })
      .catch(() => {
        /* DEV-only affordance; its absence must never break /settings */
      });
  }

  return section;
}
